import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import {
  addDays,
  addMonths,
  addWeeks,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isValid,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subMonths,
  subWeeks,
} from 'date-fns';
import { useAuth } from '@/hooks/useAuth';
import { ensureUserAccount } from '@/services/accountService';
import { getActiveAddons } from '@/services/addonsService';
import { getClients } from '@/services/clientService';
import {
  createScheduledJob,
  deleteScheduledJob,
  getScheduledJobs,
  updateScheduledJob,
} from '@/services/jobService';
import AppShell, { PageLoading } from '@/components/AppShell';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

function toLocalInputValue(date) {
  const d = new Date(date);
  if (!Number.isFinite(d.getTime())) return '';

  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

function safeParseDate(value) {
  if (!value || typeof value !== 'string') return null;

  try {
    const parsed = parseISO(value);
    return isValid(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function safeFormatDate(value, pattern, fallback = 'Invalid date') {
  const parsed = safeParseDate(value);
  if (!parsed) return fallback;
  return format(parsed, pattern);
}

const INITIAL_FORM = {
  id: null,
  clientId: '',
  title: '',
  serviceType: '',
  startAt: '',
  endAt: '',
  durationMinutes: '60',
  notes: '',
  status: 'scheduled',
  recurringFrequency: 'none',
  recurringUntil: '',
};

const CORE_SERVICES = [
  { id: 'core:lawn-mowing', name: 'Lawn Mowing' },
  { id: 'core:edging', name: 'Edging' },
  { id: 'core:trimming', name: 'String Trimming' },
  { id: 'core:blowing', name: 'Blowing' },
  { id: 'core:weed-control', name: 'Weed Control' },
  { id: 'core:fertilization', name: 'Fertilization' },
  { id: 'core:aeration', name: 'Aeration' },
  { id: 'core:cleanup', name: 'Cleanup' },
];

function getInitialForm(baseDate = new Date()) {
  const start = startOfDay(baseDate);
  start.setHours(9, 0, 0, 0);
  const end = new Date(start);
  end.setMinutes(end.getMinutes() + 60);

  return {
    ...INITIAL_FORM,
    startAt: toLocalInputValue(start),
    endAt: toLocalInputValue(end),
  };
}

function getDatePart(value) {
  return value ? value.slice(0, 10) : '';
}

function getTimePart(value) {
  return value ? value.slice(11, 16) : '';
}

function combineLocalDateTime(datePart, timePart) {
  if (!datePart || !timePart) return '';
  return `${datePart}T${timePart}`;
}

function addMinutesToLocalInput(value, minutes) {
  if (!value || !minutes) return '';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return '';

  date.setMinutes(date.getMinutes() + Number(minutes));
  return toLocalInputValue(date);
}

function getDurationMinutes(startAt, endAt) {
  const start = new Date(startAt);
  const end = new Date(endAt);
  if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime()) || end <= start) {
    return '60';
  }

  return String(Math.round((end.getTime() - start.getTime()) / 60000));
}

const STATUS_LABELS = {
  scheduled: 'Scheduled',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

const RECURRING_LABELS = {
  none: 'Does not repeat',
  daily: 'Daily',
  weekly: 'Weekly',
  'bi-weekly': 'Bi-weekly',
  monthly: 'Monthly',
};

export default function Schedule() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [account, setAccount] = useState(null);
  const [clients, setClients] = useState([]);
  const [serviceOptions, setServiceOptions] = useState(CORE_SERVICES);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [calendarView, setCalendarView] = useState('month');
  const [activeDate, setActiveDate] = useState(new Date());
  const [form, setForm] = useState(() => getInitialForm());

  const visibleRange = useMemo(() => {
    if (calendarView === 'day') {
      return {
        start: startOfDay(activeDate),
        end: addDays(startOfDay(activeDate), 1),
      };
    }

    if (calendarView === 'week') {
      const start = startOfWeek(activeDate, { weekStartsOn: 0 });
      return {
        start,
        end: addDays(start, 7),
      };
    }

    const start = startOfWeek(startOfMonth(activeDate), { weekStartsOn: 0 });
    const end = endOfWeek(endOfMonth(activeDate), { weekStartsOn: 0 });
    return { start, end };
  }, [activeDate, calendarView]);

  const loadData = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);

      const result = await ensureUserAccount(user);
      const accountId = result?.account?.id;
      setAccount(result?.account || null);

      if (!accountId) {
        setClients([]);
        setJobs([]);
        return;
      }

      const [clientList, scheduledJobs, activeAddons] = await Promise.all([
        getClients(accountId),
        getScheduledJobs(accountId, {
          startDate: visibleRange.start.toISOString(),
          endDate: visibleRange.end.toISOString(),
        }),
        getActiveAddons(accountId),
      ]);

      setClients(clientList);
      setJobs(scheduledJobs);
      setServiceOptions(buildServiceOptions(activeAddons));
    } catch (err) {
      console.error('[Schedule] Error loading data:', err);
      setError(err.message || 'Failed to load scheduling data');
    } finally {
      setLoading(false);
    }
  }, [user, visibleRange.start, visibleRange.end]);

  useEffect(() => {
    if (!authLoading && user) {
      loadData();
    } else if (!authLoading && !user) {
      navigate('/login');
    }
  }, [authLoading, user, navigate, loadData]);

  const jobsByDay = useMemo(() => {
    const map = new Map();
    for (const job of jobs) {
      const parsedStart = safeParseDate(job.start_at);
      if (!parsedStart) continue;

      const key = format(parsedStart, 'yyyy-MM-dd');
      const dayJobs = map.get(key) || [];
      dayJobs.push(job);
      dayJobs.sort((a, b) => new Date(a.start_at) - new Date(b.start_at));
      map.set(key, dayJobs);
    }
    return map;
  }, [jobs]);

  const visibleDays = useMemo(() => {
    return eachDayOfInterval({
      start: visibleRange.start,
      end: addDays(visibleRange.end, -1),
    });
  }, [visibleRange]);

  const handleDateShift = (dir) => {
    if (calendarView === 'day') {
      setActiveDate((prev) => addDays(prev, dir));
      return;
    }

    if (calendarView === 'week') {
      setActiveDate((prev) => (dir > 0 ? addWeeks(prev, 1) : subWeeks(prev, 1)));
      return;
    }

    setActiveDate((prev) => (dir > 0 ? addMonths(prev, 1) : subMonths(prev, 1)));
  };

  const resetForm = () => {
    setForm(getInitialForm(activeDate));
  };

  const startEdit = (job) => {
    setForm({
      id: job.id,
      clientId: job.client_id,
      title: job.title || '',
      serviceType: job.service_type || '',
      startAt: toLocalInputValue(job.start_at),
      endAt: toLocalInputValue(job.end_at),
      durationMinutes: getDurationMinutes(job.start_at, job.end_at),
      notes: job.notes || '',
      status: job.status || 'scheduled',
      recurringFrequency: job.recurring_frequency || 'none',
      recurringUntil: job.recurring_until ? toLocalInputValue(job.recurring_until) : '',
    });
  };

  const validateForm = () => {
    if (!form.clientId || !form.serviceType || form.serviceType.trim().length === 0 || !form.startAt || !form.durationMinutes) {
      throw new Error('Choose a client, service, date, start time, and duration to schedule this job.');
    }

    if (Number(form.durationMinutes) <= 0) {
      throw new Error('Duration must be greater than zero.');
    }

    if (form.recurringFrequency !== 'none' && form.recurringUntil && new Date(form.recurringUntil) < new Date(form.startAt)) {
      throw new Error('Recurring end date must be after the first job start time');
    }
  };

  const updateStartAt = (nextStartAt) => {
    setForm((prev) => ({
      ...prev,
      startAt: nextStartAt,
      endAt: addMinutesToLocalInput(nextStartAt, prev.durationMinutes),
    }));
  };

  const updateDuration = (durationMinutes) => {
    setForm((prev) => ({
      ...prev,
      durationMinutes,
      endAt: addMinutesToLocalInput(prev.startAt, durationMinutes),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!account?.id) return;

    try {
      validateForm();
      setSaving(true);
      setError(null);

      const payload = {
        accountId: account.id,
        clientId: form.clientId,
        title: form.title,
        serviceType: form.serviceType.trim(),
        notes: form.notes,
        status: form.status,
        startAt: new Date(form.startAt).toISOString(),
        endAt: new Date(form.endAt).toISOString(),
        recurringFrequency: form.recurringFrequency,
        recurringUntil: form.recurringUntil ? new Date(form.recurringUntil).toISOString() : null,
      };

      if (form.id) {
        await updateScheduledJob(form.id, payload);
      } else {
        await createScheduledJob(payload);
      }

      resetForm();
      await loadData();
    } catch (err) {
      console.error('[Schedule] Error saving job:', err);
      setError(err.message || 'Failed to save job');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (jobId) => {
    if (!window.confirm('Delete this scheduled job?')) return;

    try {
      setError(null);
      await deleteScheduledJob(jobId);
      await loadData();
    } catch (err) {
      console.error('[Schedule] Error deleting job:', err);
      setError(err.message || 'Failed to delete job');
    }
  };

  if (authLoading || loading) {
    return <PageLoading label="Loading schedule..." />;
  }

  return (
    <AppShell
      title="Service Calendar"
      description="Schedule client jobs, track recurring work, and review day, week, or month views."
      accountName={account?.name}
      userLabel={user?.user_metadata?.full_name || user?.email}
    >
      <div className="space-y-6">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle>{form.id ? 'Edit Service Visit' : 'Quick Schedule'}</CardTitle>
            <CardDescription>Pick who, what day, what time, and how long. GreenQuote handles the end time.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <div>
                <Label>Client</Label>
                <ClientCombobox
                  clients={clients}
                  value={form.clientId}
                  onChange={(value) => setForm((prev) => ({ ...prev, clientId: value }))}
                />
              </div>

              <div>
                <Label htmlFor="serviceType">Service</Label>
                <ServiceMultiSelect
                  options={serviceOptions}
                  value={form.serviceType}
                  onChange={(value) => setForm((prev) => ({ ...prev, serviceType: value }))}
                />
              </div>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div>
                <Label htmlFor="startDate">Date</Label>
                <Input
                  id="startDate"
                  type="date"
                  className="mt-1"
                  value={getDatePart(form.startAt)}
                  onChange={(e) => updateStartAt(combineLocalDateTime(e.target.value, getTimePart(form.startAt) || '09:00'))}
                />
              </div>

              <div>
                <Label htmlFor="startTime">Start Time</Label>
                <Input
                  id="startTime"
                  type="time"
                  className="mt-1"
                  value={getTimePart(form.startAt)}
                  onChange={(e) => updateStartAt(combineLocalDateTime(getDatePart(form.startAt), e.target.value))}
                />
              </div>

              <div>
                <Label>Duration</Label>
                <Select value={form.durationMinutes} onValueChange={updateDuration}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="30">30 minutes</SelectItem>
                    <SelectItem value="45">45 minutes</SelectItem>
                    <SelectItem value="60">1 hour</SelectItem>
                    <SelectItem value="90">1.5 hours</SelectItem>
                    <SelectItem value="120">2 hours</SelectItem>
                    <SelectItem value="180">3 hours</SelectItem>
                  </SelectContent>
                </Select>
                {form.endAt && (
                  <p className="mt-1 text-xs text-slate-500">Ends at {format(new Date(form.endAt), 'p')}</p>
                )}
              </div>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <Label>Recurring</Label>
                <Select value={form.recurringFrequency} onValueChange={(value) => setForm((prev) => ({ ...prev, recurringFrequency: value }))}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="bi-weekly">Bi-weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
                <p className="mt-1 text-xs text-slate-500">
                  Repeats on the calendar until the end date. Leave blank to repeat until deleted.
                </p>
              </div>

              <div>
                <Label htmlFor="recurringUntil">Repeat Until</Label>
                <Input
                  id="recurringUntil"
                  type="date"
                  className="mt-1"
                  value={getDatePart(form.recurringUntil)}
                  onChange={(e) => setForm((prev) => ({ ...prev, recurringUntil: e.target.value ? `${e.target.value}T23:59` : '' }))}
                  disabled={form.recurringFrequency === 'none'}
                />
              </div>
              </div>

              {form.id && (
                <div>
                  <Label>Status</Label>
                  <Select value={form.status} onValueChange={(value) => setForm((prev) => ({ ...prev, status: value }))}>
                    <SelectTrigger className="mt-1 max-w-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="scheduled">Scheduled</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="md:col-span-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  className="mt-1"
                  value={form.notes}
                  onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
                  placeholder="Gate code, crew notes, special instructions..."
                  rows={3}
                />
              </div>

              <div className="flex flex-wrap gap-2">
                <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800" disabled={saving}>
                  {saving ? 'Saving...' : form.id ? 'Update Visit' : 'Schedule Visit'}
                </Button>
                {form.id && (
                  <Button type="button" variant="outline" onClick={resetForm}>
                    Cancel Edit
                  </Button>
                )}
              </div>
            </form>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <CardTitle>Calendar</CardTitle>
                <CardDescription>
                  {calendarView === 'month' && format(activeDate, 'MMMM yyyy')}
                  {calendarView === 'week' && `${format(startOfWeek(activeDate), 'MMM d')} - ${format(addDays(startOfWeek(activeDate), 6), 'MMM d, yyyy')}`}
                  {calendarView === 'day' && format(activeDate, 'EEEE, MMM d, yyyy')}
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => handleDateShift(-1)}>←</Button>
                <Button variant="outline" onClick={() => setActiveDate(new Date())}>Today</Button>
                <Button variant="outline" onClick={() => handleDateShift(1)}>→</Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Tabs value={calendarView} onValueChange={setCalendarView}>
              <TabsList className="mb-4">
                <TabsTrigger value="day">Day</TabsTrigger>
                <TabsTrigger value="week">Week</TabsTrigger>
                <TabsTrigger value="month">Month</TabsTrigger>
              </TabsList>

              <TabsContent value="month">
                <div className="grid grid-cols-7 gap-2 text-xs text-slate-500 mb-2">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
                    <div key={d} className="font-semibold">{d}</div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-2">
                  {visibleDays.map((day) => {
                    const dayKey = format(day, 'yyyy-MM-dd');
                    const dayJobs = jobsByDay.get(dayKey) || [];
                    const inCurrentMonth = day.getMonth() === activeDate.getMonth();

                    return (
                      <button
                        key={dayKey}
                        type="button"
                        onClick={() => {
                          setActiveDate(day);
                          setCalendarView('day');
                        }}
                        className={`min-h-28 rounded-md border border-slate-200 p-2 text-left hover:bg-emerald-50 ${inCurrentMonth ? 'bg-white' : 'bg-slate-50 text-slate-400'}`}
                      >
                        <p className="text-xs font-semibold mb-1">{format(day, 'd')}</p>
                        <div className="space-y-1">
                          {dayJobs.slice(0, 3).map((job) => (
                            <div key={job.occurrence_key || job.id} className="text-xs bg-emerald-100 text-emerald-900 rounded px-1 py-0.5 truncate">
                              {safeFormatDate(job.start_at, 'p')} · {job.service_type}
                            </div>
                          ))}
                          {dayJobs.length > 3 && (
                            <p className="text-xs text-gray-500">+{dayJobs.length - 3} more</p>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </TabsContent>

              <TabsContent value="week">
                <div className="space-y-3">
                  {visibleDays.map((day) => {
                    const dayKey = format(day, 'yyyy-MM-dd');
                    const dayJobs = jobsByDay.get(dayKey) || [];

                    return (
                      <div key={dayKey} className="rounded-md border border-slate-200 p-3">
                        <p className="font-semibold text-slate-800 mb-2">{format(day, 'EEEE, MMM d')}</p>
                        {dayJobs.length === 0 ? (
                          <p className="text-sm text-gray-500">No jobs</p>
                        ) : (
                          <div className="space-y-2">
                            {dayJobs.map((job) => (
                              <JobRow key={job.occurrence_key || job.id} job={job} onEdit={startEdit} onDelete={handleDelete} onOpenClient={() => navigate('/clients')} />
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </TabsContent>

              <TabsContent value="day">
                <div className="space-y-2">
                  {(jobsByDay.get(format(activeDate, 'yyyy-MM-dd')) || []).length === 0 ? (
                    <p className="text-sm text-gray-500">No jobs scheduled for this day.</p>
                  ) : (
                    (jobsByDay.get(format(activeDate, 'yyyy-MM-dd')) || []).map((job) => (
                      <JobRow key={job.occurrence_key || job.id} job={job} onEdit={startEdit} onDelete={handleDelete} onOpenClient={() => navigate('/clients')} />
                    ))
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
}

function buildServiceOptions(activeAddons = []) {
  const seen = new Set();
  const services = [];

  for (const service of CORE_SERVICES) {
    const key = service.name.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      services.push(service);
    }
  }

  for (const addon of activeAddons) {
    const name = addon?.name?.trim();
    if (!name) continue;

    const key = name.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      services.push({ id: `addon:${addon.id}`, name });
    }
  }

  return services;
}

function getClientLabel(client) {
  if (!client) return '';
  return client.customer_name || client.property_address || 'Unnamed client';
}

function getSelectedServices(value) {
  return value
    ? value.split(',').map((item) => item.trim()).filter(Boolean)
    : [];
}

function ClientCombobox({ clients, value, onChange }) {
  const [open, setOpen] = useState(false);
  const selectedClient = clients.find((client) => client.id === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="mt-1 w-full justify-between font-normal"
        >
          <span className="truncate">
            {selectedClient ? getClientLabel(selectedClient) : 'Search or select a client'}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search clients..." />
          <CommandList className="max-h-72">
            <CommandEmpty>No clients found.</CommandEmpty>
            <CommandGroup>
              {clients.map((client) => (
                <CommandItem
                  key={client.id}
                  value={`${getClientLabel(client)} ${client.property_address || ''} ${client.customer_email || ''}`}
                  onSelect={() => {
                    onChange(client.id);
                    setOpen(false);
                  }}
                >
                  <Check className={cn('mr-2 h-4 w-4', value === client.id ? 'opacity-100' : 'opacity-0')} />
                  <span className="min-w-0">
                    <span className="block truncate">{getClientLabel(client)}</span>
                    {client.property_address && (
                      <span className="block truncate text-xs text-slate-500">{client.property_address}</span>
                    )}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function ServiceMultiSelect({ options, value, onChange }) {
  const [open, setOpen] = useState(false);
  const selectedServices = getSelectedServices(value);

  const toggleService = (serviceName) => {
    const exists = selectedServices.includes(serviceName);
    const next = exists
      ? selectedServices.filter((item) => item !== serviceName)
      : [...selectedServices, serviceName];

    onChange(next.join(', '));
  };

  return (
    <div className="mt-1 space-y-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between font-normal"
          >
            <span className="truncate">
              {selectedServices.length > 0
                ? `${selectedServices.length} service${selectedServices.length === 1 ? '' : 's'} selected`
                : 'Select one or more services'}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
          <Command>
            <CommandInput placeholder="Search services..." />
            <CommandList className="max-h-72">
              <CommandEmpty>No services found.</CommandEmpty>
              <CommandGroup>
                {options.map((service) => {
                  const selected = selectedServices.includes(service.name);
                  return (
                    <CommandItem
                      key={service.id}
                      value={service.name}
                      onSelect={() => toggleService(service.name)}
                    >
                      <Check className={cn('mr-2 h-4 w-4', selected ? 'opacity-100' : 'opacity-0')} />
                      {service.name}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {selectedServices.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedServices.map((service) => (
            <Badge key={service} variant="secondary" className="gap-1 bg-emerald-50 text-emerald-800">
              {service}
              <button
                type="button"
                onClick={() => toggleService(service)}
                className="rounded-sm text-emerald-700 hover:text-emerald-950"
                aria-label={`Remove ${service}`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

function JobRow({ job, onEdit, onDelete, onOpenClient }) {
  return (
    <div className="border rounded-md p-3 bg-white flex flex-col md:flex-row md:items-center md:justify-between gap-3">
      <div>
        <p className="font-medium text-gray-900">{job.title || job.service_type}</p>
        <p className="text-sm text-gray-600">
          {safeFormatDate(job.start_at, 'p')} - {safeFormatDate(job.end_at, 'p')} · {job.service_type}
        </p>
        <p className="text-sm text-gray-500">
          Client: {job.client?.customer_name || 'Client'} · {job.client?.property_address || 'No address'}
        </p>
        {job.recurring_frequency && job.recurring_frequency !== 'none' && (
        <p className="text-xs text-emerald-700 mt-1">Repeats {RECURRING_LABELS[job.recurring_frequency] || job.recurring_frequency}</p>
        )}
        {job.notes && <p className="text-sm text-gray-500 mt-1">{job.notes}</p>}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center rounded-full px-2 py-1 text-xs bg-emerald-100 text-emerald-900">
          {STATUS_LABELS[job.status] || job.status}
        </span>
        <Button variant="outline" size="sm" onClick={onOpenClient}>View Client</Button>
        <Button variant="outline" size="sm" onClick={() => onEdit(job)}>Edit</Button>
        <Button variant="outline" size="sm" onClick={() => onDelete(job.id)} className="text-red-600 border-red-200 hover:bg-red-50">
          Delete
        </Button>
      </div>
    </div>
  );
}
